import { deleteField, GeoPoint } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  MapPin,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  Component,
  lazy,
  Suspense,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { FileInput } from "components/ui/file-input";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { storage } from "integrations/firebase/storage";

import { mapsLocationUrl } from "../../event-experience/directions";
import { ownedFloorPlanPaths } from "../../event-experience/floor-plans";
import type { Venue } from "../../event-experience/schemas";
import { useAdminVenues } from "../use-admin-content";
import { useEntityWrites, type EntityFields } from "../use-entity-writes";

import { venueSpec } from "./content-specs";
import { EntityCrudSection } from "./EntityCrudSection";
import {
  nominatimGate,
  readMapInput,
  readPin,
  splitCoordinatePair,
  type AddressMatch,
  type Pin,
} from "./venue-location";

// Leaflet and its stylesheet are about 150 kB, and only an organiser editing a
// venue ever needs them, so they load when the editor opens.
//
// React.lazy keeps a rejected import for good, so after one failed chunk load
// (a deploy that replaced the chunk, a dropped connection) every later render
// would throw the same error. Each retry therefore makes a new lazy component
// around a fresh import() call. A browser that caches the failed module itself
// still needs a page reload, which the fallback also offers.
function lazyVenuePinMap() {
  return lazy(() => import("./VenuePinMap"));
}

/** A warning in the status style: tinted fill and edge, ink text, an icon. */
const WARNING_NOTE =
  "flex items-start gap-2 rounded-md border border-status-warning bg-status-warning/15 px-3 py-2 text-sm text-foreground";

/**
 * Leaflet's touch handling is chosen for a coarse pointer, not for a narrow
 * screen: a small laptop window still has a mouse, and a tablet is wide.
 */
const COARSE_POINTER = "(pointer: coarse)";

function subscribeToPointer(onChange: () => void) {
  if (typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(COARSE_POINTER);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function useCoarsePointer(): boolean {
  return useSyncExternalStore(
    subscribeToPointer,
    () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia(COARSE_POINTER).matches,
    () => false,
  );
}

/**
 * The map is the only part of the editor loaded on demand, so it is the only
 * part a failed chunk load can take down. This keeps that failure inside the
 * map's box and leaves the coordinate inputs to do the map's job.
 */
class MapLoadBoundary extends Component<
  { children: ReactNode; onRetry: () => void; onFailed: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onFailed();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        className="space-y-3 rounded-lg border border-border bg-muted p-4 text-sm text-foreground"
        data-testid="venue-map-failed"
      >
        <p>
          The map could not be loaded. Enter the coordinates under &ldquo;Enter
          coordinates instead&rdquo; below, or try the map again. If it still
          fails, reload the page after saving your changes.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-11"
            data-testid="venue-map-retry"
            onClick={() => {
              this.setState({ failed: false });
              this.props.onRetry();
            }}
          >
            Try the map again
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11"
            onClick={() => window.location.reload()}
          >
            Reload the page
          </Button>
        </div>
      </div>
    );
  }
}

/** firestore.rules caps floorPlanImages at 20 entries. */
const MAX_FLOOR_PLANS = 20;
/** storage.rules: isBelowSize(15 MB) && isImage(). */
const MAX_BYTES = 15 * 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];

/**
 * Venues: the shared list and delete, with one editor for everything a venue
 * holds.
 *
 * `location` is a GeoPoint set on a map and `floorPlanImages` is a list of
 * Storage paths, and neither fits EntityCrudSection's form of strings, numbers
 * and booleans. They used to sit in a second dialog behind a row button, next
 * to the generic edit form; the organiser asked why editing a venue took two
 * dialogs, so the spec's `editor` replaces the generic form with this one.
 *
 * Every write goes through the venue update key list `['name','address',
 * 'description','location','floorPlanImages']`, so no rules change.
 */
export function VenuesSection({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const venues = useAdminVenues(eventId);

  return (
    <EntityCrudSection
      eventId={eventId}
      uid={uid}
      items={venues.status === "success" ? venues.data : []}
      loading={venues.status === "loading"}
      error={venues.status === "error" ? venues.error : undefined}
      reload={venues.reload}
      spec={{
        ...venueSpec,
        secondary: (venue) =>
          [venue.address, venue.location ? "Pinned on map" : undefined]
            .filter(Boolean)
            .join(" · "),
        editor: ({ item, onClose, onSaved }) => (
          <VenueEditor
            eventId={eventId}
            venue={item}
            onClose={onClose}
            onSaved={onSaved}
          />
        ),
      }}
    />
  );
}

type Lookup =
  | { status: "idle" }
  | { status: "searching" }
  | {
      status: "found";
      /** What was typed, and the shorter query that found it if it differs. */
      typed: string;
      query: string;
      matches: AddressMatch[];
      chosen: number;
    }
  | { status: "none" }
  | { status: "failed" };

/** What the last press of "Set pin" on the paste field came to. */
type PasteStatus =
  | { status: "idle" }
  | { status: "placed"; pin: Pin }
  | { status: "problem"; text: string };

function VenueEditor({
  eventId,
  venue,
  onClose,
  onSaved,
}: {
  eventId: string;
  /** Null when adding a venue. */
  venue: Venue | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const writes = useEntityWrites(eventId, "venues");
  const fileInput = useRef<HTMLInputElement>(null);
  // Set once the venue exists: at mount when editing, after the first save
  // when adding. Floor plans need it, because their Storage path names it.
  const [venueId, setVenueId] = useState<string | null>(venue?.id ?? null);
  const [justCreated, setJustCreated] = useState(false);
  const [name, setName] = useState(venue?.name ?? "");
  const [address, setAddress] = useState(venue?.address ?? "");
  const [description, setDescription] = useState(venue?.description ?? "");
  // The pin is held as the text of the two coordinate inputs, which the map
  // writes into. One source of truth, so typing and dragging cannot disagree.
  const [latitude, setLatitude] = useState(
    venue?.location ? String(venue.location.latitude) : "",
  );
  const [longitude, setLongitude] = useState(
    venue?.location ? String(venue.location.longitude) : "",
  );
  // The pin the map shows. It follows the inputs whenever they read as a pin
  // or as none, and holds still while they read as neither, so clearing a
  // field to retype it does not drop the marker and then drop it again.
  const [mapPin, setMapPin] = useState<Pin | null>(
    venue?.location
      ? {
          latitude: venue.location.latitude,
          longitude: venue.location.longitude,
        }
      : null,
  );
  // What the last save wrote, to tell an organiser closing the editor that a
  // change has not been saved. Floor plans are not in here: each one saves
  // as soon as it uploads.
  const [savedFields, setSavedFields] = useState(() => ({
    name: venue?.name ?? "",
    address: venue?.address ?? "",
    description: venue?.description ?? "",
    latitude: venue?.location ? String(venue.location.latitude) : "",
    longitude: venue?.location ? String(venue.location.longitude) : "",
  }));
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [coordinatesOpen, setCoordinatesOpen] = useState(false);
  // A new lazy component per attempt; see lazyVenuePinMap.
  const [VenuePinMap, setVenuePinMap] = useState(lazyVenuePinMap);
  const touch = useCoarsePointer();
  const [lookup, setLookup] = useState<Lookup>({ status: "idle" });
  const [pasted, setPasted] = useState("");
  const [pasteStatus, setPasteStatus] = useState<PasteStatus>({
    status: "idle",
  });
  // The saved list, kept locally so an upload or a removal shows immediately;
  // the list behind the dialog refreshes through onSaved.
  const [plans, setPlans] = useState<string[]>(() =>
    venue
      ? ownedFloorPlanPaths(eventId, venue.id, venue.floorPlanImages ?? [])
      : [],
  );
  // How many stored paths this editor is not showing. `isValidVenue` checks
  // only the length of floorPlanImages, so a path belonging to another venue
  // or nested below this one can be in there, and `upload` and `remove` write
  // the array back whole, which drops it. Read once at mount, since the
  // editor is remounted per venue and `plans` moves underneath it.
  const [hiddenPlans] = useState(() =>
    venue
      ? (venue.floorPlanImages ?? []).length -
        ownedFloorPlanPaths(eventId, venue.id, venue.floorPlanImages ?? [])
          .length
      : 0,
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const parsedPin = readPin(latitude, longitude);
  const pin: Pin | null = typeof parsedPin === "object" ? parsedPin : null;
  const busy = writes.saving || uploading;
  // What a participant's Directions button opens when there is no pin; null
  // while the address is blank.
  const mapsCheckUrl = mapsLocationUrl(address);
  const dirty =
    name !== savedFields.name ||
    address !== savedFields.address ||
    description !== savedFields.description ||
    latitude !== savedFields.latitude ||
    longitude !== savedFields.longitude;

  function setCoordinates(nextLatitude: string, nextLongitude: string) {
    setLatitude(nextLatitude);
    setLongitude(nextLongitude);
    const read = readPin(nextLatitude, nextLongitude);
    if (typeof read !== "string") setMapPin(read);
  }

  function placePin(next: Pin | null) {
    setCoordinates(
      next ? String(next.latitude) : "",
      next ? String(next.longitude) : "",
    );
  }

  /** Close, unless that would throw away a change; then say so once. */
  function requestClose() {
    if (dirty && !confirmingDiscard) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  }

  /**
   * Reads what was pasted and, if it holds a place, moves the pin there.
   * An empty field after a pin was set from it does nothing: the field is
   * empty by then, and the next tap on Set pin or Enter must not replace
   * "Pin set at..." with a complaint. An empty field at any other time asks
   * for something to paste.
   */
  function placePastedPin(text: string) {
    if (
      !text.trim() &&
      pasteStatus.status === "placed" &&
      pin &&
      pin.latitude === pasteStatus.pin.latitude &&
      pin.longitude === pasteStatus.pin.longitude
    )
      return;
    const reading = readMapInput(text);
    if ("problem" in reading) {
      setPasteStatus({ status: "problem", text: reading.problem });
      return;
    }
    placePin(reading.pin);
    setLookup({ status: "idle" });
    setPasted("");
    setPasteStatus({ status: "placed", pin: reading.pin });
  }

  async function lookUpAddress() {
    // The button is disabled while a lookup runs; Enter is not, and the gate
    // below drops what gets past this.
    if (lookup.status === "searching") return;
    if (!address.trim()) {
      setLookup({ status: "idle" });
      setProblem("Enter an address to look up.");
      return;
    }
    setProblem(null);
    setLookup({ status: "searching" });
    try {
      const found = await nominatimGate.lookUp(address);
      if (found === "busy") {
        // A lookup started from an editor that has since closed still holds
        // the page-wide gate. Leave the button usable for the next press.
        setLookup({ status: "idle" });
        return;
      }
      if (!found) {
        setLookup({ status: "none" });
        return;
      }
      placePin(found.matches[0]);
      setLookup({
        status: "found",
        typed: address.trim(),
        query: found.query,
        matches: found.matches,
        chosen: 0,
      });
    } catch {
      setLookup({ status: "failed" });
    }
  }

  /** Mirrors the rules through venueSpec, so a refusal is a sentence. */
  function firstProblem(): string | null {
    const values: Record<string, string> = { name, address, description };
    for (const field of venueSpec.fields) {
      const text = (values[field.name] ?? "").trim();
      if (field.required && !text) return `${field.label} is required.`;
      if (field.maxLength && text.length > field.maxLength)
        return `${field.label} must be ${field.maxLength} characters or fewer.`;
    }
    return typeof parsedPin === "string" ? parsedPin : null;
  }

  async function save() {
    const invalid = firstProblem();
    setProblem(invalid);
    if (invalid) return;
    const location = pin ? new GeoPoint(pin.latitude, pin.longitude) : null;

    if (!venueId) {
      // Optional keys are left out rather than sent empty or deleted:
      // deleteField() is an error in a create, and a create has nothing to
      // clear.
      const fields: EntityFields = {
        name: name.trim(),
        address: address.trim(),
      };
      if (description.trim()) fields.description = description.trim();
      if (location) fields.location = location;
      const id = await writes.create(fields, {
        timestampFields: ["createdAt"],
      });
      if (id) {
        // Stay open: floor plans upload under the venue's id, which exists
        // only now.
        setVenueId(id);
        setJustCreated(true);
        setSavedFields({ name, address, description, latitude, longitude });
        setConfirmingDiscard(false);
        onSaved();
      }
      return;
    }

    // A blank pin REMOVES the field. `location == null || location is latlng`
    // is the rule, and there is no empty GeoPoint: 0,0 is the Gulf of Guinea,
    // not "unset", and the attendee card would offer directions to it.
    const ok = await writes.update(venueId, {
      name: name.trim(),
      address: address.trim(),
      description: description.trim(),
      location: location ?? deleteField(),
    });
    if (ok) {
      onSaved();
      onClose();
    }
  }

  async function upload(file: File) {
    if (!venueId) return;
    if (plans.length >= MAX_FLOOR_PLANS) {
      setProblem(`A venue can hold ${MAX_FLOOR_PLANS} floor plans.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setProblem("Floor plans must be 15 MB or smaller.");
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setProblem("Floor plans must be a PNG, JPEG or WebP image.");
      return;
    }
    const filename = storageFilename(file.name);
    if (!filename) {
      setProblem("That file name cannot be used. Rename the file and retry.");
      return;
    }
    const path = `events/${eventId}/venues/${venueId}/${filename}`;
    // The backend refuses a floorPlanImages array with a repeated path when it
    // sweeps the blobs on DELETE_VENUE, so catch the collision here rather than
    // making the venue undeletable.
    if (plans.includes(path)) {
      setProblem("A floor plan with that file name is already uploaded.");
      return;
    }
    setProblem(null);
    setUploading(true);
    try {
      await uploadBytes(ref(storage, path), file, { contentType: file.type });
      const next = [...plans, path];
      const ok = await writes.update(venueId, { floorPlanImages: next });
      if (ok) {
        setPlans(next);
        onSaved();
      } else {
        // The blob is in Storage and the array does not name it, so nothing
        // collects it: DELETE_VENUE sweeps only the paths the array holds and
        // storage.rules denies the client delete. It is bounded, since the
        // path comes from the file name and the same file uploaded again
        // overwrites that object and records it, but until someone retries it
        // sits there, so say which retry is the safe one.
        setProblem(
          "The floor plan uploaded but could not be saved to the venue. Try the same file again: it reuses the stored file rather than leaving a second copy behind.",
        );
      }
    } catch (err) {
      setProblem(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function remove(path: string) {
    if (!venueId) return;
    const next = plans.filter((existing) => existing !== path);
    // Only the reference goes. storage.rules denies delete on this path, and
    // the blob is swept by DELETE_VENUE's trigger, the same bargain the legacy
    // app made, where removing a plan only dropped it from the array.
    const ok = await writes.update(venueId, { floorPlanImages: next });
    if (ok) {
      setPlans(next);
      onSaved();
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && requestClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{venueId ? "Edit venue" : "Add venue"}</DialogTitle>
        </DialogHeader>

        <div
          className="max-h-[60vh] space-y-5 overflow-y-auto pr-1"
          data-testid="venue-editor"
        >
          <div className="space-y-1.5">
            <Label htmlFor="venue-name">Name *</Label>
            <Input
              id="venue-name"
              testId="venue-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="venue-address">Address *</Label>
            <Input
              id="venue-address"
              testId="venue-address"
              // Not "street-address": the browser would offer the organiser's
              // own home address for a venue's.
              autoComplete="off"
              aria-describedby="venue-address-help"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                // The matches and "Found by searching for" describe the old
                // text, so they go once the address changes.
                if (lookup.status !== "searching")
                  setLookup({ status: "idle" });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void lookUpAddress();
                }
              }}
            />
            <p
              id="venue-address-help"
              className="text-xs text-muted-foreground"
            >
              Write it as you would on an envelope. When a participant taps
              Directions, Google Maps looks this text up, so a pin is not
              needed.
            </p>
            <div className="flex flex-wrap gap-2">
              {mapsCheckUrl ? (
                <Button asChild variant="outline" className="h-11">
                  <a
                    href={mapsCheckUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="venue-check-maps"
                  >
                    Check in Google Maps
                    <ExternalLink className="h-4 w-4" />
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </Button>
              ) : (
                // aria-disabled rather than disabled, so a screen reader still
                // finds it and reads why; out of the tab order, since there is
                // nothing it can do yet.
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 aria-disabled:pointer-events-none aria-disabled:opacity-50"
                  aria-disabled="true"
                  aria-describedby="venue-check-maps-hint"
                  tabIndex={-1}
                  data-testid="venue-check-maps"
                >
                  Check in Google Maps
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                className="h-11"
                disabled={lookup.status === "searching"}
                aria-describedby="venue-find-help"
                data-testid="venue-find-address"
                onClick={() => void lookUpAddress()}
              >
                {lookup.status === "searching" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Find on map
              </Button>
            </div>
            {!mapsCheckUrl && (
              <p
                id="venue-check-maps-hint"
                className="text-xs text-muted-foreground"
              >
                Type an address first
              </p>
            )}
            <p id="venue-find-help" className="text-xs text-muted-foreground">
              Optional: place a pin on the map
            </p>
            <div aria-live="polite" data-testid="venue-lookup-live">
              <LookupMessage lookup={lookup} />
            </div>
            {lookup.status === "found" && (
              <LookupMatches
                matches={lookup.matches}
                chosen={lookup.chosen}
                onChoose={(index) => {
                  placePin(lookup.matches[index]);
                  setLookup({ ...lookup, chosen: index });
                }}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="venue-description">Description</Label>
            <Textarea
              id="venue-description"
              data-testid="venue-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Shown on the venue card: what the building is, how to find the
              entrance, anything a map pin does not say.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground">
                Map pin (optional)
              </p>
              {(latitude.trim() || longitude.trim()) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-11"
                  data-testid="venue-clear-pin"
                  onClick={() => {
                    placePin(null);
                    setLookup({ status: "idle" });
                    setPasteStatus({ status: "idle" });
                  }}
                >
                  <X className="h-4 w-4" />
                  Remove pin
                </Button>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="venue-paste-map">
                Paste a map link or coordinates
              </Label>
              <div className="flex gap-2">
                <Input
                  id="venue-paste-map"
                  testId="venue-paste-map"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="e.g. 52.5200, 13.4050"
                  aria-describedby="venue-paste-status"
                  aria-invalid={pasteStatus.status === "problem"}
                  value={pasted}
                  className="min-w-0 flex-1"
                  onChange={(e) => {
                    setPasted(e.target.value);
                    setPasteStatus({ status: "idle" });
                  }}
                  onPaste={(e) => {
                    // A paste here is a whole link or pair, so it is read at
                    // once rather than waiting for a second tap.
                    const text = e.clipboardData.getData("text");
                    if (!text.trim()) return;
                    e.preventDefault();
                    setPasted(text);
                    placePastedPin(text);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      placePastedPin(pasted);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 shrink-0"
                  data-testid="venue-paste-apply"
                  onClick={() => placePastedPin(pasted)}
                >
                  Set pin
                </Button>
              </div>
              {/* Only a problem is announced from here. When the pin is set,
                  the pin status below the map is the one polite region that
                  speaks, and this sentence is for the eye. */}
              <div
                id="venue-paste-status"
                aria-live="polite"
                data-testid="venue-paste-live"
              >
                {pasteStatus.status === "problem" && (
                  <p
                    className="text-sm text-foreground"
                    data-testid="venue-paste-result"
                  >
                    {pasteStatus.text}
                  </p>
                )}
              </div>
              {pasteStatus.status === "placed" &&
                pin &&
                pin.latitude === pasteStatus.pin.latitude &&
                pin.longitude === pasteStatus.pin.longitude && (
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="venue-paste-result"
                  >
                    Pin set at {pin.latitude}, {pin.longitude}. Drag it on the
                    map to adjust.
                  </p>
                )}
            </div>
            <MapLoadBoundary
              onRetry={() => setVenuePinMap(lazyVenuePinMap)}
              onFailed={() => setCoordinatesOpen(true)}
            >
              <Suspense
                fallback={
                  <div className="flex h-64 items-center justify-center rounded-lg border border-border bg-muted sm:h-72">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                }
              >
                <VenuePinMap
                  pin={mapPin}
                  touch={touch}
                  testId="venue-map"
                  onChange={(next) => {
                    placePin(next);
                    setLookup({ status: "idle" });
                  }}
                />
              </Suspense>
            </MapLoadBoundary>
            <p
              className="text-xs text-muted-foreground"
              data-testid="venue-pin-status"
              aria-live="polite"
            >
              {pin ? (
                <>
                  <MapPin className="mr-1 inline h-3 w-3" />
                  Pinned at {pin.latitude}, {pin.longitude}. The participant map
                  opens this pin instead of searching for the address. Drag it
                  to move it.
                </>
              ) : typeof parsedPin === "string" ? (
                parsedPin
              ) : touch ? (
                "No pin. Tap the map to place one, or use Find on map. Use two fingers to move the map. Without a pin, Directions search for the address."
              ) : (
                "No pin. Click the map to place one, or use Find on map. Without a pin, Directions search for the address."
              )}
            </p>
            <details
              className="text-sm"
              open={coordinatesOpen}
              onToggle={(e) => setCoordinatesOpen(e.currentTarget.open)}
            >
              <summary
                className="flex min-h-11 cursor-pointer items-center text-sm text-muted-foreground"
                data-testid="venue-coordinates-toggle"
              >
                Enter coordinates instead
              </summary>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="venue-latitude">Latitude</Label>
                  <Input
                    id="venue-latitude"
                    testId="venue-latitude"
                    inputMode="decimal"
                    value={latitude}
                    placeholder="52.520008"
                    onChange={(e) => {
                      // A pasted "52.52, 13.40" fills both inputs.
                      const pair = splitCoordinatePair(e.target.value);
                      if (pair) setCoordinates(pair[0], pair[1]);
                      else setCoordinates(e.target.value, longitude);
                      setLookup({ status: "idle" });
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="venue-longitude">Longitude</Label>
                  <Input
                    id="venue-longitude"
                    testId="venue-longitude"
                    inputMode="decimal"
                    value={longitude}
                    placeholder="13.404954"
                    onChange={(e) => {
                      setCoordinates(latitude, e.target.value);
                      setLookup({ status: "idle" });
                    }}
                  />
                </div>
              </div>
            </details>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-medium text-foreground">
              Floor plans ({plans.length}/{MAX_FLOOR_PLANS})
            </p>
            {!venueId ? (
              <p
                className="text-xs text-muted-foreground"
                data-testid="venue-floor-plans-after-save"
              >
                Save the venue first. Floor plans can be added straight after.
              </p>
            ) : (
              <>
                {justCreated && (
                  <p
                    className="text-sm text-foreground"
                    data-testid="venue-created"
                  >
                    Venue added. Upload its floor plans here, or close.
                  </p>
                )}
                {hiddenPlans > 0 && (
                  <p
                    className={WARNING_NOTE}
                    data-testid="venue-floor-plans-pruned"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {hiddenPlans === 1
                        ? "One stored floor plan does not belong to this venue and is not listed here. Uploading or removing a plan will drop it."
                        : `${hiddenPlans} stored floor plans do not belong to this venue and are not listed here. Uploading or removing a plan will drop them.`}{" "}
                      Dropping them is what lets this venue be deleted: the
                      deletion command refuses a venue whose list holds a
                      foreign or nested path.
                    </span>
                  </p>
                )}
                {plans.length > 0 && (
                  <ul className="space-y-1.5" data-testid="venue-floor-plans">
                    {plans.map((path) => {
                      const filename = path.slice(path.lastIndexOf("/") + 1);
                      return (
                        <li
                          key={path}
                          className="flex items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2"
                        >
                          <span className="truncate text-sm text-muted-foreground">
                            {filename}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 shrink-0"
                            disabled={busy}
                            aria-label={`Remove floor plan ${filename}`}
                            data-testid={`remove-floor-plan-${filename}`}
                            onClick={() => void remove(path)}
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="venue-floor-plan">Add a floor plan</Label>
                  <FileInput
                    id="venue-floor-plan"
                    ref={fileInput}
                    testId="venue-floor-plan"
                    disabled={busy}
                    accept={ALLOWED_TYPES.join(",")}
                    onChange={(e) => {
                      const chosen = e.target.files?.[0];
                      if (chosen) void upload(chosen);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    <Upload className="mr-1 inline h-3 w-3" />
                    PNG, JPEG or WebP, up to 15 MB each. A plan is saved as soon
                    as it uploads.
                  </p>
                </div>
              </>
            )}
          </div>

          {confirmingDiscard && dirty && (
            <p
              role="alert"
              className={WARNING_NOTE}
              data-testid="venue-unsaved-warning"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {venueId
                  ? "The venue details or the pin have changes that are not saved. Save them, or discard them and close."
                  : "This venue is not saved yet. Save it, or discard it and close."}
              </span>
            </p>
          )}

          {(problem || writes.error) && (
            <p
              className="text-sm text-destructive-text"
              data-testid="venue-form-error"
            >
              {problem ?? writes.error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={requestClose}
            data-testid="close-venue"
          >
            {confirmingDiscard && dirty
              ? "Discard and close"
              : justCreated && !dirty
                ? "Close"
                : "Cancel"}
          </Button>
          <Button
            variant="cta"
            onClick={save}
            disabled={busy || (justCreated && !dirty)}
            data-testid="save-venue"
          >
            {writes.saving
              ? "Saving…"
              : justCreated
                ? dirty
                  ? "Save changes"
                  : "Saved"
                : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One sentence, the only part of the lookup a screen reader announces. */
function LookupMessage({ lookup }: { lookup: Lookup }) {
  if (lookup.status === "idle" || lookup.status === "searching") return null;
  let text: ReactNode;
  if (lookup.status === "none") {
    text =
      "Nothing found on OpenStreetMap. The address still works for directions, because Google Maps looks it up when a participant taps Directions. To place a pin as well, try just the street, house number and town, for example \u201cGänsheidestraße 26, Stuttgart\u201d, or paste a map link below.";
  } else if (lookup.status === "failed") {
    text =
      "The address lookup is not reachable right now. The address still works for directions. To place a pin, tap the map or paste a map link below.";
  } else {
    const chosen = lookup.matches[lookup.chosen];
    text = (
      <>
        {chosen.area
          ? `Only the area was found: ${chosen.label}. The pin is at its middle, so drag it to the venue.`
          : `Pin placed at ${chosen.label}. Drag it if the entrance is somewhere else.`}
        {lookup.query !== lookup.typed && (
          <> Found by searching for &ldquo;{lookup.query}&rdquo;.</>
        )}
      </>
    );
  }
  return (
    <p
      className="text-xs text-muted-foreground"
      data-testid="venue-lookup-result"
    >
      {text}
    </p>
  );
}

function LookupMatches({
  matches,
  chosen,
  onChoose,
}: {
  matches: AddressMatch[];
  chosen: number;
  onChoose: (index: number) => void;
}) {
  if (matches.length < 2) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        Not the right place? Pick another match:
      </p>
      <ul className="space-y-1" data-testid="venue-lookup-matches">
        {matches.map((match, index) => (
          <li key={`${match.latitude},${match.longitude},${match.label}`}>
            <button
              type="button"
              aria-pressed={index === chosen}
              data-testid={`venue-lookup-match-${index}`}
              onClick={() => onChoose(index)}
              className="flex min-h-11 w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-xs text-foreground hover:bg-muted aria-pressed:border-lit aria-pressed:bg-lit/10 aria-pressed:font-medium"
            >
              <MapPin
                className={
                  index === chosen
                    ? "h-4 w-4 shrink-0"
                    : "h-4 w-4 shrink-0 opacity-30"
                }
              />
              <span>{match.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A single path segment, because everything downstream assumes one.
 *
 * storage.rules matches `events/{eventId}/venues/{venueId}/{filename}`, the
 * attendee side's `ownedFloorPlanPaths` drops any path with a slash after the
 * prefix, and the deletion repository refuses the venue outright if one is
 * there. A slash in the name would therefore upload a file the attendee cannot
 * see and the venue cannot be deleted around.
 *
 * Letters and digits in any script, because this is a name a person reads back
 * in the list. `\w` is ASCII-only however the regex is flagged, so it turned
 * `Erdgeschoß.png` and `会議室.png` into rows of underscores. Object names are
 * UTF-8 and the single-segment rule is the only constraint that matters.
 */
export function storageFilename(name: string): string | null {
  const cleaned = name
    .replace(/[^\p{L}\p{N}._\- ]+/gu, "_")
    .replace(/^[.\s]+/, "")
    .trim();
  return cleaned.length > 0 && cleaned.length <= 200 ? cleaned : null;
}
